import React, { useState, useMemo } from "react";
import { 
  User, CreditCard, Clock, Search, Loader2, 
  ArrowUpDown, ArrowUp, ArrowDown // Import thêm icon
} from "lucide-react";
import getBankInfo from '../../../../services/service';

const formatCurrency = (value) => {
  if (!value || isNaN(parseFloat(value))) return "0 ₫";
  return new Intl.NumberFormat("vi-VN", {
    style: "currency",
    currency: "VND",
  }).format(parseFloat(value));
};

const formatDateTime = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  return date.toLocaleString("vi-VN", {
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const CustomerTable = ({ customers, loading }) => {
  // State quản lý việc sắp xếp
  const [sortConfig, setSortConfig] = useState({
    key: null, // 'number_transaction' hoặc 'totalAmount'
    direction: 'desc' // 'asc' (tăng dần) hoặc 'desc' (giảm dần)
  });

  // Hàm xử lý khi click vào tiêu đề cột
  const handleSort = (key) => {
    let direction = 'desc';
    
    // Nếu đang sort cột này rồi thì đảo chiều
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  // Logic sắp xếp dữ liệu (Client-side)
  const sortedCustomers = useMemo(() => {
    let sortableItems = [...customers];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        // Lấy giá trị để so sánh, nếu null/undefined thì coi là 0
        const aValue = parseFloat(a[sortConfig.key] || 0);
        const bValue = parseFloat(b[sortConfig.key] || 0);

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [customers, sortConfig]);

  // Component con hiển thị Icon sort
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ArrowUpDown size={14} className="ml-1 text-slate-300" />;
    if (sortConfig.direction === 'asc') return <ArrowUp size={14} className="ml-1 text-blue-600" />;
    return <ArrowDown size={14} className="ml-1 text-blue-600" />;
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500 font-semibold tracking-wider">
            <th className="px-6 py-4">Thông tin Khách hàng</th>
            <th className="px-6 py-4">Ngân hàng</th>
            
            {/* Cột Số GD - Có thể click để sort */}
            <th 
              className="px-6 py-4 text-center cursor-pointer hover:bg-slate-100 transition-colors select-none group"
              onClick={() => handleSort('number_transaction')}
            >
              <div className="flex items-center justify-center">
                Số GD
                <SortIcon columnKey="number_transaction" />
              </div>
            </th>

            {/* Cột Tổng chi tiêu - Có thể click để sort */}
            <th 
              className="px-6 py-4 text-right cursor-pointer hover:bg-slate-100 transition-colors select-none group"
              onClick={() => handleSort('totalAmount')}
            >
              <div className="flex items-center justify-end">
                Tổng chi tiêu
                <SortIcon columnKey="totalAmount" />
              </div>
            </th>

            <th className="px-6 py-4 text-right">Giao dịch cuối</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {loading ? (
            <tr>
              <td colSpan="5" className="px-6 py-20 text-center">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 size={32} className="text-blue-500 animate-spin" />
                  <p className="text-slate-500">Đang tải dữ liệu...</p>
                </div>
              </td>
            </tr>
          ) : sortedCustomers.length > 0 ? (
            // Sử dụng sortedCustomers thay vì customers gốc
            sortedCustomers.map((customer, index) => {
              const bankInfo = getBankInfo(customer.bank_code);
              return (
                <tr
                  key={index}
                  className="hover:bg-slate-50 transition-colors group"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-bold border border-slate-200 flex-shrink-0">
                        {customer.customerName ? (
                          customer.customerName.charAt(0)
                        ) : (
                          <User size={18} />
                        )}
                      </div>
                      <div>
                        <p
                          className="text-sm font-bold text-slate-900 line-clamp-1 max-w-[200px]"
                          title={customer.customerName}
                        >
                          {customer.customerName || "Khách vãng lai"}
                        </p>
                        <p className="text-xs text-slate-500 font-mono mt-0.5">
                          {customer.customer_account}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div
                      className={`inline-flex items-center gap-2 px-2 py-1 rounded text-xs font-medium ${bankInfo.bg} ${bankInfo.color} whitespace-nowrap`}
                    >
                      {bankInfo.logo ? (
                        <img
                          src={bankInfo.logo}
                          alt={bankInfo.name}
                          className="h-4 object-contain"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <CreditCard size={12} />
                      )}
                      {bankInfo.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-block px-3 py-1 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium">
                      {customer.number_transaction}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <p className="text-sm font-bold text-slate-900">
                      {formatCurrency(customer.totalAmount)}
                    </p>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex flex-col items-end gap-0.5">
                      <div className="flex items-center gap-1.5 text-sm text-slate-700">
                        <Clock size={14} className="text-slate-400" />
                        {formatDateTime(customer.last_tx_time).split(" ")[0]}
                      </div>
                      <span className="text-xs text-slate-400">
                        {formatDateTime(customer.last_tx_time).split(" ")[1]}
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                <div className="flex flex-col items-center">
                  <Search size={40} className="text-slate-200 mb-3" />
                  <p>Không tìm thấy dữ liệu.</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CustomerTable;